import { Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormArray, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OnboardingLinkAddon } from 'voxloud-types/dist/models';
import { ExtendedOnboardingLinkAddon } from '../../models/activate-link.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const availableAddons: ExtendedOnboardingLinkAddon[] = [
  {
    id: 'extra-phone-numbers',
    quantity: 5,
    name: 'Extra Phone Numbers',
    min: 1
  },
  {
    id: 'call-recording',
    quantity: 1,
    name: 'Call Recording',
    min: 1,
    max: 1
  },
  {
    id: 'advanced-analytics',
    quantity: 1,
    name: 'Advanced Analytics',
    min: 1,
    max: 1
  },
  {
    id: 'video-conferencing',
    quantity: 10,
    name: 'Video Conferencing',
    min: 1
  },
  {
    id: 'crm-integration',
    quantity: 1,
    name: 'CRM Integration',
    min: 1,
    max: 1
  },
  {
    id: 'international-calling',
    quantity: 6,
    name: 'International Calling',
    min: 1,
    max: 10
  },
  {
    id: 'premium-support',
    quantity: 1,
    name: 'Premium Support',
    min: 1,
    max: 1
  }
];


@Component({
  selector: 'gdp-activation-link-addon-selector',
  templateUrl: './activation-link-addon-selector.component.html',
  styleUrls: ['./activation-link-addon-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ActivationLinkAddonSelectorComponent),
      multi: true
    }
  ]
})
export class ActivationLinkAddonSelectorComponent implements ControlValueAccessor, OnDestroy {
  @Input() availableAddons: ExtendedOnboardingLinkAddon[] = [];

  addonsFormArray: FormArray<FormControl<ExtendedOnboardingLinkAddon>>;

  disabled = false;
  searchText = '';
  filteredAddons: ExtendedOnboardingLinkAddon[] = [];
  private destroy$ = new Subject<void>();
  private isAddingAddon = false;

  get selectableAddons(): ExtendedOnboardingLinkAddon[] {
    const selectedIds = this.addonsFormArray.value.map(a => a?.id);
    return this.availableAddons.filter(a => !selectedIds.includes(a.id));
  }

  private onChange: (value: OnboardingLinkAddon[]) => void = () => { };
  private onTouched: () => void = () => { };

  constructor() {
    this.addonsFormArray = new FormArray<FormControl<ExtendedOnboardingLinkAddon>>([]);

    this.addonsFormArray.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isAddingAddon) {
          this.emitChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toExtended(addon: OnboardingLinkAddon): ExtendedOnboardingLinkAddon {
    const available = this.availableAddons.find(a => a.id === addon.id);
    return {
      ...addon,
      name: available?.name || addon.id,
      min: available?.min || 1,
      max: available?.max
    };
  }

  private toBase(addon: ExtendedOnboardingLinkAddon): OnboardingLinkAddon {
    const { name, min, max, ...baseAddon } = addon;
    return baseAddon;
  }

  writeValue(value: OnboardingLinkAddon[]): void {
    this.addonsFormArray.clear({ emitEvent: false });
    (value || []).forEach(addon => {
      const extended = this.toExtended(addon);
      this.addonsFormArray.push(this.createAddonFormControl(extended), { emitEvent: false });
    });
  }

  registerOnChange(fn: (value: OnboardingLinkAddon[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.addonsFormArray.disable({ emitEvent: false });
    } else {
      this.addonsFormArray.enable({ emitEvent: false });
    }
  }

  private emitChanges(): void {
    const addons = this.addonsFormArray.value
      .filter(a => a != null)
      .map(a => this.toBase(a!));
    this.onChange(addons);
    this.onTouched();
  }

  private createAddonFormControl(addon: ExtendedOnboardingLinkAddon): FormControl<ExtendedOnboardingLinkAddon> {
    return new FormControl(addon, { nonNullable: true });
  }

  removeAddon(index: number): void {
    this.addonsFormArray.removeAt(index);
  }

  selectAddon(addon: ExtendedOnboardingLinkAddon): void {
    const newAddon: ExtendedOnboardingLinkAddon = {
      ...addon,
      quantity: this.getAddonQuantity(addon)
    };
    this.isAddingAddon = true;
    this.addonsFormArray.push(this.createAddonFormControl(newAddon));

    // Clear search text after selection
    this.searchText = '';
    this.updateFilteredAddons();

    // Emit changes manually once, then reset flag after child component initialization
    setTimeout(() => {
      this.isAddingAddon = false;
      this.emitChanges();
    }, 0);
  }

  onSearchChange(): void {
    this.updateFilteredAddons();
  }

  onInputFocus(): void {
    this.updateFilteredAddons();
  }

  private updateFilteredAddons(): void {
    const searchLower = this.searchText.toLowerCase().trim();

    if (!searchLower) {
      this.filteredAddons = [...this.selectableAddons];
    } else {
      this.filteredAddons = this.selectableAddons.filter(addon =>
        addon.name.toLowerCase().includes(searchLower) ||
        addon.id.toLowerCase().includes(searchLower)
      );
    }
  }

  trackByIndex(_index: number, control: FormControl<ExtendedOnboardingLinkAddon>): string {
    return control.value.id;
  }

  private getAddonQuantity(addon: ExtendedOnboardingLinkAddon): number {
    if (addon.quantity < addon.min)
      return addon.min;
    if (addon.max != null && addon.quantity > addon.max)
      return addon.max;
    return addon.quantity;
  }
}
